// THE REVIEW LANE: why a review did or did not fan out, what the lead's related-code
// prefetch produced, and the group fan-out payload itself.
// Split out of `../hive-event.ts`, which is now the barrel over this directory.

import { z } from 'zod';
import { count, HIVE_EVENT_MAX_ITEMS, HIVE_EXCERPT_MAX_CHARS, line } from './bounds';
import { HiveEvent } from './kind';
import { HiveRoundPayloadSchema } from './round';

/** Why a review did or did not fan out — the audited fact, never inferred from counts. */
export const HiveReviewOutcome = {
  /** The diff was under both floors; the lead was never called. */
  BelowFloor: 'below_floor',
  /** No planner seam was wired (no model brain, or the box opted out). */
  NoPlanner: 'no_planner',
  /** The lead answered, and nothing in the answer survived the parse gate. */
  PlanUnusable: 'plan_unusable',
  /** The lead threw. Costs the split, never the review. */
  PlanFailed: 'plan_failed',
  /** The diff was split and reviewed by group. */
  FannedOut: 'fanned_out',
} as const;
export type HiveReviewOutcome = (typeof HiveReviewOutcome)[keyof typeof HiveReviewOutcome];

/** The single most actionable cause that left files unread. */
export const HiveReviewCoverageGap = {
  /** A group reviewer did not return an answer, and did not on a retry either. Ours, and retryable. */
  ReviewerFailed: 'reviewer_failed',
  /** The rendered diff hit its ceiling before reaching these files. A budget defect. */
  Truncated: 'truncated',
  /** The lead's plan left changed files in no group at all. A planning defect. */
  Unassigned: 'unassigned',
} as const;
export type HiveReviewCoverageGap =
  (typeof HiveReviewCoverageGap)[keyof typeof HiveReviewCoverageGap];

/**
 * Why the lead's RELATED-CODE prefetch (#2551) handed the reviewers nothing, or a
 * cut block. The producer's own vocabulary is `@developerz/review`
 * `RelatedContextGap`; the runner's `fanout.test.ts` pins that every member of it
 * is a member of this, so the wire cannot refuse a gap the lane can produce.
 */
export const HiveRelatedContextGap = {
  /** No structural port was handed to the review — no index on the box, or the seam is unwired. */
  Absent: 'absent',
  /** The diff named nothing the extractor could ask about. */
  NoSymbols: 'no_symbols',
  /** The index answered and nothing outside the held files came back. */
  NoneFound: 'none_found',
  /** The explore threw or refused. Ours; the operator can act on it. */
  LookupFailed: 'lookup_failed',
  /** The prefetch outlived its time-box; the members ran without it. */
  Timeout: 'timeout',
  /** Rendered, but cut at the block's budget. */
  Truncated: 'truncated',
  /**
   * The explores answered, and the SOURCE of the callers they named was not read
   * before the time-box expired — the reviewer saw which code calls the change
   * and not what that code does. Distinct from {@link Timeout}, which is the
   * index not answering at all.
   */
  CallersCut: 'callers_cut',
  /**
   * The prefetch was cut by a box DRAIN (SIGTERM, a self-update swap), not by the
   * time-box. Distinct from {@link Timeout}: it says nothing about the index.
   * Widened here a release before any box wrote it (#7647, readers in #7665); since
   * release 2 runners write it as detected. A box older than that build still
   * reports a drain as `timeout`.
   */
  Drained: 'drained',
} as const;
export type HiveRelatedContextGap =
  (typeof HiveRelatedContextGap)[keyof typeof HiveRelatedContextGap];

/** What the lead's related-code prefetch produced, summed over the groups it ran for. */
export const ReviewRelatedContextSchema = z.object({
  symbols: count().describe('symbol names the diff named and the lead asked the index about'),
  definitions: count().describe('out-of-diff DEFINITIONS rendered into <related_code>'),
  call_sites: count().describe('out-of-diff CALL SITES rendered into <related_code>'),
  chars: count().describe('chars of <related_code> handed to the file groups, summed'),
  gap: z
    .enum(Object.values(HiveRelatedContextGap))
    .nullable()
    .describe('the most actionable cause the block is absent or cut. null = rendered in full.'),
  ms: count().describe("wall clock the prefetch cost inside the lead's window"),
});
export type ReviewRelatedContext = z.infer<typeof ReviewRelatedContextSchema>;

/**
 * WHY THE TRIAGE FENCE REFUSED A SKIP the review lead proposed — the wire half of
 * `@developerz/review` `ReviewSkipRefusal`.
 *
 * Restated here for the reason {@link HiveReviewCoverageGap} is: this package may not
 * import `@developerz/review` (the dependency runs the other way), so the wire keeps
 * its own copy and the runner's `fanout.test.ts` pins that every member of the lane's
 * vocabulary is a member of this one — the wire can never refuse a refusal the fence
 * can produce.
 */
export const HiveReviewSkipRefusal = {
  /** The path named no changed file — the shape a hallucinated path arrives in. */
  UnknownPath: 'unknown_path',
  /** The path names auth / secrets / permissions / an env file. Never skippable. */
  SecuritySeam: 'security_seam',
  /** One more skip would take the review under the coverage floor. */
  BelowFloor: 'below_floor',
  /** The lead both grouped and skipped it; the reading wins. */
  AlsoGrouped: 'also_grouped',
  /** The same path proposed twice. */
  Duplicate: 'duplicate',
} as const;
export type HiveReviewSkipRefusal =
  (typeof HiveReviewSkipRefusal)[keyof typeof HiveReviewSkipRefusal];

/**
 * WHAT TRIAGE DECIDED on one review: how many changed files the lead declined to have
 * read, and every refusal the fence issued, by kind.
 *
 * `refused` is TOTAL over {@link HiveReviewSkipRefusal} — a `z.record` over the enum
 * rather than a partial map — because the value of the counter is the alert on a kind
 * climbing off zero, and a kind that is absent on a healthy review is
 * indistinguishable from one absent because the binary predates it.
 */
export const ReviewTriageSchema = z.object({
  skipped: count().describe('changed files the lead declined to have read, and the fence admitted'),
  refused: z
    .record(z.enum(Object.values(HiveReviewSkipRefusal)), count())
    .describe('skips the fence refused, by kind. Total over the vocabulary, zeros included.'),
});
export type ReviewTriage = z.infer<typeof ReviewTriageSchema>;

/** One group reviewer that did not come back. */
export const ReviewerFailureSchema = z.object({
  key: line().describe("the group's key, so the failure lines up with the files it cost"),
  attempts: z.number().int().min(1).describe('attempts spent before giving up'),
  error: line(HIVE_EXCERPT_MAX_CHARS).describe('the LAST rejection, bounded'),
});
export type ReviewerFailure = z.infer<typeof ReviewerFailureSchema>;

/**
 * THE REVIEW LANE'S FAN-OUT DECISION
 * (`apps/runner/src/modes/worker/review/bun-adapter.ts` `emitFanOut`).
 *
 * Measured baseline (prod, 30 days to 2026-09-02): 1,496 rows, **873 of them
 * `below_floor`** — the lead was never called — and no round or lead timing on the lane at
 * all. The round fields it gains here are the reason it is a {@link HiveRoundPayloadSchema}
 * and not a bare object: rule 8's window is unmeasured on this lane precisely because
 * nothing on the event could carry the measurement.
 */
export const ReviewFanOutPayloadSchema = HiveRoundPayloadSchema.extend({
  event: z.literal(HiveEvent.ReviewFanOut),
  outcome: z
    .enum(Object.values(HiveReviewOutcome))
    .describe('why this review did or did not fan out. Audited, never inferred from the counts.'),
  /** Changed files in scope after ignores and path filters. */
  files: count().describe('hive.md rule 1: changed files the lead had to partition'),
  /** Characters the rendered diff would occupy — what the floor and the ceiling both measure. */
  rendered_chars: count().describe(
    'size of the rendered diff — what both the floor and the ceiling grade',
  ),
  /** Groups dispatched as the file partition — the lead's, plus any the sweep opened. */
  planned: count().describe('hive.md rule 1: groups the lead planned'),
  /**
   * Changed files the lead's plan left in NO group, placed by the runner's sweep (#4263).
   *
   * Zero on a healthy plan and on every arm no lead was called on. Non-zero is a lead
   * defect that cost nothing — the files were reviewed — and it is the ONLY trace of one:
   * before the sweep those files rode `unreviewed_unassigned` into a partial review that
   * failed the review gate, so the fix that made them reviewed also made the lead's
   * omission rate unobservable. This is what keeps it observable.
   */
  swept_up: count().describe(
    "hive.md rule 1: changed files the lead's plan left in no group, placed by the sweep",
  ),
  /** Groups whose reviewer returned an answer. */
  reviewed: count().describe('hive.md rule 2: groups whose reviewer returned an answer'),
  /** The concurrency ceiling this box was configured with. */
  max_reviewers: z.number().int().min(1).describe('hive.md rule 5: the reviewer ceiling'),
  /** The finding budget every reviewer of this fan-out was stated. */
  max_findings_per_reviewer: count().describe(
    "the finding budget each reviewer was stated — the model's own, not divided by group count",
  ),
  /**
   * The one most actionable cause that left files unread, or `null` when a reviewer read
   * the whole diff. Nullable and not optional: "one reviewer read everything" is the only
   * shape that can claim complete coverage, and it must be a value the producer wrote.
   */
  coverage_gap: z
    .enum(Object.values(HiveReviewCoverageGap))
    .nullable()
    .describe('the most actionable cause files went unread. null = complete coverage, proven.'),
  /** Files nobody read. */
  unreviewed_files: count().describe('files no reviewer read'),
  /** The three causes, each counted — `coverage_gap` names only the outranking one. */
  unreviewed_reviewer_failed: count().describe(
    'unread because their reviewer died. Ours, retryable.',
  ),
  unreviewed_truncated: count().describe(
    'unread because the render stopped short. A budget defect.',
  ),
  unreviewed_unassigned: count().describe(
    'unread because the plan left them out. A planning defect.',
  ),
  reviewer_failures: z
    .array(ReviewerFailureSchema)
    .max(HIVE_EVENT_MAX_ITEMS)
    .describe(
      'hive.md rule 2: WHY each reviewer died. Its absence is what made the defect undiagnosable.',
    ),
  reviewer_failures_truncated: count().describe('reviewer failures beyond the cap'),
  /** #2551: the lead's related-code prefetch, on every arm. */
  related_context: ReviewRelatedContextSchema,
  /** What the lead DECLINED to have read, and every skip the fence refused. */
  triage: ReviewTriageSchema,
});
export type ReviewFanOutPayload = z.infer<typeof ReviewFanOutPayloadSchema>;
